import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import {
  getCurrentUser,
  fetchUserAttributes,
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';

interface AuthUser {
  userId: string;
  username: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ isSignedIn: boolean }>;
  logout: () => Promise<void>;
  register: (email: string, password: string, username: string) => Promise<unknown>;
  confirmRegistration: (email: string, code: string) => Promise<unknown>;
  forgotPassword: (email: string) => Promise<unknown>;
  resetUserPassword: (email: string, code: string, newPassword: string) => Promise<unknown>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function resolveUser(): Promise<AuthUser | null> {
  try {
    const [cognitoUser, attrs] = await Promise.all([
      getCurrentUser(),
      fetchUserAttributes(),
    ]);
    const username = attrs.preferred_username ?? attrs.email ?? cognitoUser.username;
    return { userId: cognitoUser.userId, username };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    resolveUser()
      .then(setUser)
      .finally(() => setLoading(false));
  }, []);

  // ── Inactivity auto-logout (20 minutes) ──────────────────────────
  const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutes
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (user) {
      timerRef.current = setTimeout(async () => {
        console.log('Session expired due to inactivity');
        await signOut();
        setUser(null);
        window.location.href = '/login';
      }, INACTIVITY_TIMEOUT);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    const handler = () => resetTimer();

    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetTimer(); // start the timer

    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, resetTimer]);

  async function login(email: string, password: string) {
    const normalizedEmail = email.toLowerCase();
    let result;
    try {
      result = await signIn({ username: normalizedEmail, password });
    } catch (err: unknown) {
      // Amplify throws this when a previous session wasn't fully cleared
      if ((err as { name?: string }).name === 'UserAlreadyAuthenticatedException') {
        await signOut();
        result = await signIn({ username: normalizedEmail, password });
      } else {
        throw err;
      }
    }
    if (result.isSignedIn) {
      const resolved = await resolveUser();
      setUser(resolved);
    }
    return result;
  }

  async function logout() {
    await signOut();
    setUser(null);
  }

  async function register(email: string, password: string, username: string) {
    const normalizedEmail = email.toLowerCase();
    return signUp({
      username: normalizedEmail,
      password,
      options: { userAttributes: { email: normalizedEmail, preferred_username: username } },
    });
  }

  async function confirmRegistration(email: string, code: string) {
    return confirmSignUp({ username: email.toLowerCase(), confirmationCode: code });
  }

  async function forgotPassword(email: string) {
    return resetPassword({ username: email.toLowerCase() });
  }

  async function resetUserPassword(email: string, code: string, newPassword: string) {
    return confirmResetPassword({ username: email, confirmationCode: code, newPassword });
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, register, confirmRegistration, forgotPassword, resetUserPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
